import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { toast } from 'react-hot-toast';
import { updateUserProfile } from '../../services/api';
import { 
  Store, Settings, Users, Gift, TrendingUp, DollarSign, 
  Award, Target, Calendar, Plus, Edit, Percent, History,
  Eye, LogOut, ArrowLeft, Menu, X, Bell, Search, Filter,
  ChevronDown, BarChart3, ShoppingCart, CreditCard, Star,
  Receipt, ShoppingBag, User, QrCode, Scan
} from 'lucide-react';

const SharedHeader = ({ 
  title = "Business Dashboard", 
  subtitle = "Business Dashboard",
  showBackButton = false,
  onBack = null,
  onMenuAction = null,
  pendingClaimCount = 0,
}) => {
  const { currentUser, logout } = useAuth();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [showDropdownMenu, setShowDropdownMenu] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdownMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const businessConfig = {
    businessName: currentUser?.displayName || 'My Business',
    businessEmail: currentUser?.email || 'business@example.com',
  };

  const handleMenuAction = (action, data = null) => {
    setShowDropdownMenu(false);
    if (onMenuAction) {
      onMenuAction(action, data);
    }
  };

  return (
    <div className={`${theme.gradient} shadow-lg`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between py-6">
          <div className="flex items-center space-x-4">
            {showBackButton && onBack && (
              <button
                onClick={onBack}
                className="flex items-center space-x-2 bg-white bg-opacity-20 hover:bg-opacity-30 text-white px-3 py-2 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                <span>Back</span>
              </button>
            )}
            <div className="w-12 h-12 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
              <Store className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">
                {title}
              </h1>
              <p className="text-blue-100">{subtitle}</p>
            </div>
          </div>
          
          <div className="flex items-center space-x-4">
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setShowDropdownMenu(!showDropdownMenu)}
                className="flex items-center space-x-2 bg-white bg-opacity-20 hover:bg-opacity-30 text-white px-4 py-2 rounded-lg transition-colors"
              >
                <Menu className="w-4 h-4" />
                <span>Menu</span>
                <ChevronDown className="w-4 h-4" />
              </button>
              
              {showDropdownMenu && (
                <div className="absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-lg py-2 z-50">
                  <div className="px-4 py-2 border-b border-gray-100">
                    <p className="text-sm font-semibold text-gray-900">Business Actions</p>
                  </div>
                  
                  {/* <button
                    onClick={() => {
                      navigate('/business/dashboard');
                      setShowDropdownMenu(false);
                    }}
                    className="w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-50 flex items-center space-x-3"
                  >
                    <BarChart3 className="w-4 h-4" />
                    <span>Dashboard</span>
                  </button> */}
                  
                  <button
                    onClick={() => handleMenuAction('manageOffers')}
                    className="w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-50 flex items-center space-x-3"
                  >
                    <Gift className="w-4 h-4" />
                    <span>Manage Offers</span>
                  </button>
                  
                  <button
                    onClick={() => handleMenuAction('salesHistory')}
                    className="w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-50 flex items-center space-x-3"
                  >
                    <History className="w-4 h-4" />
                    <span>Sales History</span>
                  </button>
                  
                  <button
                    onClick={() => handleMenuAction('profileUpdate')}
                    className="w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-50 flex items-center space-x-3"
                  >
                    <User className="w-4 h-4" />
                    <span>Profile Update</span>
                  </button>
                  
                  <button
                    onClick={() => handleMenuAction('patchSettings')}
                    className="w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-50 flex items-center space-x-3"
                  >
                    <Settings className="w-4 h-4" />
                    <span>Patch Settings</span>
                  </button>
                  
                  <button
                    onClick={() => handleMenuAction('shopDetails')}
                    className="w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-50 flex items-center space-x-3"
                  >
                    <Store className="w-4 h-4" />
                    <span>Shop Details</span>
                  </button>
                  
                  <button
                    onClick={() => handleMenuAction('buyPoints')}
                    className="w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-50 flex items-center space-x-3"
                  >
                    <CreditCard className="w-4 h-4" />
                    <span>Buy Points</span>
                  </button>
                  
                  {/* <button
                    onClick={() => handleMenuAction('creditByMobile')}
                    className="w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-50 flex items-center space-x-3"
                  >
                    <User className="w-4 h-4" />
                    <span>Credit by Mobile</span>
                  </button> */}
                  
                 <button
                    onClick={() => handleMenuAction('claimRequests')}
                    className="w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-50 flex items-center justify-between"
                  >
                    <span className="flex items-center space-x-3">
                      <Receipt className="w-4 h-4" />
                      <span>Claim Requests</span>
                    </span>
                    {pendingClaimCount > 0 && (
                      <span className="ml-3 inline-flex items-center justify-center px-2 py-0.5 text-xs font-semibold rounded-full bg-red-500 text-white min-w-[22px]">
                        {pendingClaimCount}
                      </span>
                    )}
                  </button> 
                  
                  <button
                    onClick={() => handleMenuAction('qrScanner')}
                    className="w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-50 flex items-center space-x-3"
                  >
                    <Scan className="w-4 h-4" />
                    <span>Scan QR Code</span>
                  </button>
                  
                  <button
                    onClick={() => handleMenuAction('qrGenerator')}
                    className="w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-50 flex items-center space-x-3"
                  >
                    <QrCode className="w-4 h-4" />
                    <span>My QR Code</span>
                  </button>
                  <button onClick={() => onMenuAction('topupRequests')}>
                  <span className="flex items-center space-x-3">
                  <Receipt className="w-4 h-4" />
                <span>Top-up Requests</span>
                </span>
                    </button>
                  
                  <div className="border-t border-gray-100 mt-2 pt-2">
                    <p className="px-4 py-1 text-xs font-semibold text-gray-500 uppercase tracking-wide">Business Functions</p>
                    
                    <button 
                      onClick={() => handleMenuAction('rewardConfig')}
                      className="w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-50 flex items-center space-x-3"
                    >
                      <Award className="w-4 h-4" />
                      <span>Reward Configuration</span>
                    </button>
                    
                    {/* <button 
                      onClick={() => handleMenuAction('customerManagement')}
                      className="w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-50 flex items-center space-x-3"
                    >
                      <Users className="w-4 h-4" />
                      <span>Customer Management</span>
                    </button> */}
                    
                    {/* <button 
                      onClick={() => handleMenuAction('businessAnalytics')}
                      className="w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-50 flex items-center space-x-3"
                    >
                      <TrendingUp className="w-4 h-4" />
                      <span>Business Analytics</span>
                    </button> */}
                  </div>
                  
                  <div className="border-t border-gray-100 mt-2 pt-2">
                    <button
                      onClick={() => {
                        logout();
                        navigate('/');
                        setShowDropdownMenu(false);
                      }}
                      className="w-full text-left px-4 py-2 text-red-600 hover:bg-red-50 flex items-center space-x-3"
                    >
                      <LogOut className="w-4 h-4" />
                      <span>Logout</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Profile Update Modal */}
      {showProfileModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h3 className="text-xl font-semibold text-gray-900">Profile Update</h3>
              <button
                onClick={() => setShowProfileModal(false)}
                className="text-gray-400 hover:text-gray-600 p-1"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <form
              className="p-6 space-y-6"
              onSubmit={async (e) => {
                e.preventDefault();
                try {
                  const form = e.currentTarget;
                  const formData = new FormData(form);
                  const payload = {
                    businessName: formData.get('businessName') || '',
                    email: formData.get('email') || '',
                    phone: formData.get('phone') || '',
                    category: formData.get('category') || '',
                    registrationType: formData.get('registrationType') || '',
                    address: formData.get('address') || ''
                  };
                  await updateUserProfile(payload);
                  toast.success('Profile updated successfully!');
                  setShowProfileModal(false);
                } catch (err) {
                  console.error('Profile update failed:', err);
                  toast.error('Failed to update profile');
                }
              }}
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Business Name</label>
                  <input
                    type="text"
                    name="businessName"
                    defaultValue={currentUser?.displayName || 'My Business'}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Email Address</label>
                  <input
                    type="email"
                    name="email"
                    defaultValue={currentUser?.email || 'business@example.com'}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number</label>
                  <input
                    type="tel"
                    name="phone"
                    placeholder="+91 98765 43210"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Business Category</label>
                  <select name="category" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500">
                    <option>Restaurant</option>
                    <option>Retail</option>
                    <option>Services</option>
                    <option>Healthcare</option>
                    <option>Other</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Registration Type</label>
                  <select name="registrationType" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500">
                    <option value="prop">Proprietorship (Prop)</option>
                    <option value="non-registration">Non-Registration</option>
                    <option value="private">Private</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Address</label>
                <textarea
                  rows="3"
                  name="address"
                  placeholder="123 Business Street, City, State 12345"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                ></textarea>
              </div>
              <div className="flex justify-end space-x-4">
                <button
                  type="button"
                  onClick={() => setShowProfileModal(false)}
                  className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className={`${theme.button} px-6 py-3 rounded-lg`}
                >
                  Update Profile
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default SharedHeader;
